import { useEffect, useRef } from "react";
import "./bokeh.scss";

const WARM = [255, 184, 92];
const HOT = [255, 241, 214];
const COOL = [159, 196, 255];
const AMBER = [255, 160, 60];

const PLANES = {
  far: { rMin: 6, rMax: 12, alpha: 0.42, parallax: 5, scroll: 0.03, drift: 3 },
  mid: { rMin: 10, rMax: 20, alpha: 0.7, parallax: 12, scroll: 0.07, drift: 5 },
  near: { rMin: 22, rMax: 34, alpha: 0.22, parallax: 26, scroll: 0.13, drift: 8 },
};

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeSprite(rgb, soft) {
  const size = 128;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  const [r, gg, b] = rgb;
  if (soft) {
    g.addColorStop(0, `rgba(${r},${gg},${b},0.85)`);
    g.addColorStop(0.3, `rgba(${r},${gg},${b},0.5)`);
    g.addColorStop(0.65, `rgba(${r},${gg},${b},0.14)`);
    g.addColorStop(1, `rgba(${r},${gg},${b},0)`);
  } else {
    g.addColorStop(0, `rgba(${r},${gg},${b},1)`);
    g.addColorStop(0.5, `rgba(${r},${gg},${b},0.96)`);
    g.addColorStop(0.68, `rgba(${r},${gg},${b},0.4)`);
    g.addColorStop(1, `rgba(${r},${gg},${b},0)`);
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return c;
}

function pickColor(rand) {
  const v = rand();
  if (v < 0.72) return "warm";
  if (v < 0.88) return "hot";
  return "cool";
}

function buildOrbs() {
  const rand = mulberry32(20261001);
  const orbs = [];
  let order = 0;

  const string = (x0, y0, x1, y1, sag, count, plane, delayBase) => {
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      const jx = (rand() - 0.5) * 0.03;
      const jy = (rand() - 0.5) * 0.03;
      const p = PLANES[plane];
      orbs.push({
        x: x0 + (x1 - x0) * t + jx,
        y: y0 + (y1 - y0) * t + sag * Math.sin(Math.PI * t) + jy,
        r: p.rMin + rand() * (p.rMax - p.rMin),
        plane,
        color: pickColor(rand),
        phase: rand() * Math.PI * 2,
        speed: 0.6 + rand() * 0.8,
        delay: delayBase + i * 55 + rand() * 40,
        order: order++,
      });
    }
  };

  string(0.56, -0.04, 1.06, 0.4, 0.08, 14, "mid", 0);
  string(0.64, 0.14, 1.1, 0.66, 0.1, 12, "far", 300);
  string(0.02, 0.34, 0.52, 0.04, 0.05, 10, "far", 500);
  string(0.42, 0.62, 1.04, 0.92, -0.06, 11, "mid", 650);

  for (let i = 0; i < 16; i++) {
    const p = PLANES.far;
    orbs.push({
      x: rand(),
      y: rand(),
      r: p.rMin + rand() * (p.rMax - p.rMin),
      plane: "far",
      color: pickColor(rand),
      phase: rand() * Math.PI * 2,
      speed: 0.5 + rand() * 0.8,
      delay: 200 + rand() * 1100,
      order: order++,
    });
  }

  for (let i = 0; i < 4; i++) {
    const p = PLANES.near;
    orbs.push({
      x: 0.3 + rand() * 0.75,
      y: rand(),
      r: p.rMin + rand() * (p.rMax - p.rMin),
      plane: "near",
      color: i % 3 === 2 ? "cool" : "warm",
      phase: rand() * Math.PI * 2,
      speed: 0.4 + rand() * 0.5,
      delay: 400 + rand() * 900,
      order: order++,
    });
  }

  return orbs;
}

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

export default function BokehField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    const sprites = {
      warm: [makeSprite(WARM, true), makeSprite(WARM, false)],
      hot: [makeSprite(HOT, true), makeSprite(HOT, false)],
      cool: [makeSprite(COOL, true), makeSprite(COOL, false)],
    };
    const bloom = makeSprite(WARM, true);
    const bloomCore = makeSprite(AMBER, true);
    const orbs = buildOrbs();
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    let w = 0;
    let h = 0;
    let narrow = false;
    let raf = 0;
    let gradient = null;
    let ground = null;
    const start = performance.now();
    const pointer = { x: -9999, y: -9999, tx: -9999, ty: -9999, has: false };

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      narrow = w < 768;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gradient = ctx.createLinearGradient(0, 0, 0, h);
      gradient.addColorStop(0, "#0d1c42");
      gradient.addColorStop(0.45, "#0a1633");
      gradient.addColorStop(1, "#060c1e");
      const gx = w * 0.1;
      const gy = h * 1.02;
      ground = ctx.createRadialGradient(gx, gy, 0, gx, gy, h * 0.24);
      ground.addColorStop(0, "rgba(72, 40, 10, 0.6)");
      ground.addColorStop(0.5, "rgba(60, 34, 8, 0.3)");
      ground.addColorStop(1, "rgba(60, 34, 8, 0)");
      if (!pointer.has) {
        pointer.x = pointer.tx = w * 0.5;
        pointer.y = pointer.ty = h * 0.42;
      }
    };

    const draw = (now) => {
      const t = now - start;
      const scrollY = window.scrollY || 0;
      pointer.x += (pointer.tx - pointer.x) * 0.08;
      pointer.y += (pointer.ty - pointer.y) * 0.08;

      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
      const groundR = h * 0.24;
      ctx.fillStyle = ground;
      ctx.fillRect(w * 0.1 - groundR, h * 1.02 - groundR, groundR * 2, groundR * 2);

      ctx.globalCompositeOperation = "lighter";

      const flicker = reduce ? 0 : 0.02 * Math.sin(t * 0.0021) + 0.012 * Math.sin(t * 0.0057 + 1.3);
      const bloomR = Math.max(w, h) * 0.42;
      const cx = w * 0.1;
      const cy = h * 1.02;
      ctx.globalAlpha = 0.24 + flicker;
      ctx.drawImage(bloom, cx - bloomR, cy - bloomR, bloomR * 2, bloomR * 2);
      const coreR = h * 0.17;
      ctx.globalAlpha = 0.36 + flicker * 1.5;
      ctx.drawImage(bloomCore, cx - coreR, cy - coreR, coreR * 2, coreR * 2);

      const px = (pointer.x - w / 2) / w;
      const py = (pointer.y - h / 2) / h;
      const wrap = h + 200;

      for (let i = 0; i < orbs.length; i++) {
        const o = orbs[i];
        const p = PLANES[o.plane];
        const born = reduce ? 1 : easeOut(Math.min(1, Math.max(0, (t - o.delay) / 800)));
        if (born <= 0) continue;

        const drift = reduce ? 0 : p.drift;
        const dx = Math.sin(t * 0.0004 * o.speed + o.phase) * drift - px * p.parallax * 2;
        const dy = Math.cos(t * 0.00031 * o.speed + o.phase) * drift * 0.6 - py * p.parallax;
        const x = o.x * w + dx;
        let y = o.y * h + dy - scrollY * p.scroll;
        y = ((((y + 100) % wrap) + wrap) % wrap) - 100;

        let f = 0;
        if (pointer.has) {
          const d = Math.hypot(x - pointer.x, y - pointer.y);
          f = Math.max(0, 1 - d / 240);
          f = f * f * (3 - 2 * f);
        }

        const quiet = narrow && o.plane !== "far";
        const r = o.r * (1 + 0.18 * f) * (narrow ? 0.7 : 1);
        const a = p.alpha * (quiet ? 0.5 : 1) * born * (1 + 0.9 * f);
        const [soft, sharp] = sprites[o.color];

        ctx.globalAlpha = Math.min(1, a * (1 - f * 0.55));
        ctx.drawImage(soft, x - r * 1.6, y - r * 1.6, r * 3.2, r * 3.2);
        if (f > 0.01) {
          ctx.globalAlpha = Math.min(1, a * f);
          ctx.drawImage(sharp, x - r, y - r, r * 2, r * 2);
        }
      }
    };

    const loop = (now) => {
      if (!document.hidden) draw(now);
      raf = requestAnimationFrame(loop);
    };

    const onPointer = (e) => {
      pointer.has = true;
      pointer.tx = e.clientX;
      pointer.ty = e.clientY;
    };
    const onLeave = () => {
      pointer.tx = w * 0.5;
      pointer.ty = h * 0.42;
    };

    resize();
    window.addEventListener("resize", resize);
    if (reduce) {
      draw(start + 5000);
    } else {
      if (fine) {
        window.addEventListener("pointermove", onPointer, { passive: true });
        window.addEventListener("pointerleave", onLeave);
        document.addEventListener("mouseleave", onLeave);
      }
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas className="bokeh" ref={canvasRef} aria-hidden="true"></canvas>;
}
